import { type HandoverSpec } from '@/designs-dev/handover/types';
import ChatV2 from '@/karehero-app/tell-us-once/intake/ChatV2';
import { type ChatQuestion } from '@/karehero-app/tell-us-once/intake/content';
import {
  INTAKE_CHAT_V2_1,
  INTAKE_CHAT_V2_1_EXIT,
  INTAKE_CHAT_V2_1_MORE,
  INTAKE_CHAT_V2_1_NO_CONSENT,
  INTAKE_CHAT_V2_1_NO_NEED,
  INTAKE_CHAT_V2_1_SELF,
} from '@/karehero-app/tell-us-once/intake/content.v2-1';
import PlaceholderScreen from '@/karehero-app/tell-us-once/intake/PlaceholderScreen';
import {
  HELP_TYPED,
  LIVE_CAPTION_SCRIPT,
} from '@/karehero-app/tell-us-once/intake/content.v2-0-dev';
import {
  IntakeStyleContext,
  ONBOARDING_2_0_STYLE,
} from '@/karehero-app/tell-us-once/intake/IntakeStyle';

const noop = () => {};

// Where a turn sits in 2.1's chat, or in one of its branches.
const atIn = (questions: ChatQuestion[], id: string) =>
  questions.findIndex((q) => q.id === id);
const at = (id: string) => atIn(INTAKE_CHAT_V2_1, id);

/**
 * 2.1 with one turn's lines changed, to trial how much is left said other
 * ways. Each one is a whole chat, so the history above it is the same.
 */
const saying = (
  id: string,
  change: Pick<ChatQuestion, 'says'> | Pick<ChatQuestion, 'ask'>,
) => INTAKE_CHAT_V2_1.map((q) => (q.id === id ? { ...q, ...change } : q));

const trial = (id: string, change: Parameters<typeof saying>[1]) => () =>
  chat(saying(id, change), { startAt: at(id) });

// Drawn as Onboarding 2.0 draws its chat: the logo bar, the mic beside the
// field, and its reply style.
const chat = (
  questions: ChatQuestion[],
  {
    startAt = 0,
    keyboard = false,
    typed,
    recording,
    captions,
    sent,
    reading,
    missed,
  }: {
    startAt?: number;
    keyboard?: boolean;
    typed?: string;
    recording?: boolean;
    captions?: boolean;
    sent?: number;
    reading?: boolean;
    missed?: number;
  } = {},
) => (
  <IntakeStyleContext.Provider value={ONBOARDING_2_0_STYLE}>
    <ChatV2
      questions={questions}
      startAt={startAt}
      keyboard={keyboard}
      typed={typed}
      recording={recording}
      captionsShown={captions}
      sent={sent}
      reading={reading}
      missed={missed}
      liveCaptions={LIVE_CAPTION_SCRIPT.split(' ')}
      onExit={noop}
      account={{ onSignOut: noop }}
      inlineMic
    />
  </IntakeStyleContext.Provider>
);

/**
 * Onboarding 2.1: 2.0 with what changed after design review round 5. The happy
 * path first, end to end with the changes marked, then a section per change.
 */
export const onboarding21Spec: HandoverSpec = {
  id: 'onboarding-2-1',
  title: 'Onboarding 4.1',
  description: '',
  canvas: true,
  links: [
    { label: 'Onboarding 2.0', url: '/tell-us-once/flows/onboarding-2-0' },
  ],
  flows: [
    {
      // The whole of 2.1 end to end, with what changed after the review on
      // gardenia, above the sections that go into each change.
      title: 'Happy path',
      steps: [
        {
          id: 'happy-welcome',
          title: 'What we do, about 3 minutes',
          render: () => chat(INTAKE_CHAT_V2_1),
          highlight: 'custom.gardeniaDark',
        },
        {
          id: 'happy-help',
          title: 'The open question',
          render: () => chat(INTAKE_CHAT_V2_1, { startAt: at('help') }),
        },
        {
          id: 'happy-recording',
          title: 'Recording',
          render: () =>
            chat(INTAKE_CHAT_V2_1, { startAt: at('help'), recording: true }),
        },
        {
          id: 'happy-captions',
          title: 'Recording, captions on',
          render: () =>
            chat(INTAKE_CHAT_V2_1, {
              startAt: at('help'),
              recording: true,
              captions: true,
            }),
        },
        {
          id: 'happy-reading',
          title: 'Reading what they said',
          render: () =>
            chat(INTAKE_CHAT_V2_1, {
              startAt: at('help'),
              sent: 1,
              reading: true,
              captions: true,
            }),
        },
        {
          id: 'happy-summary',
          title: 'Does this look right?',
          render: () => chat(INTAKE_CHAT_V2_1, { startAt: at('summary') }),
        },
        {
          id: 'happy-consent',
          title: 'Consent, or Power of Attorney',
          render: () =>
            chat(INTAKE_CHAT_V2_1, { startAt: at('share-consent') }),
          highlight: 'custom.gardeniaDark',
        },
        {
          id: 'happy-location',
          title: "Where they live, and what's left",
          render: () => chat(INTAKE_CHAT_V2_1, { startAt: at('location') }),
          highlight: 'custom.gardeniaDark',
        },
        {
          id: 'happy-age',
          title: 'How old they are',
          render: () => chat(INTAKE_CHAT_V2_1, { startAt: at('age') }),
        },
        {
          id: 'happy-phone',
          title: 'Your phone number',
          render: () => chat(INTAKE_CHAT_V2_1, { startAt: at('phone') }),
        },
        {
          id: 'happy-start',
          title: 'Where to start',
          render: () => chat(INTAKE_CHAT_V2_1, { startAt: at('start') }),
        },
        {
          id: 'happy-book-call',
          title: 'Book a call page',
          render: () => (
            <PlaceholderScreen title='Book a call page' note='Placeholder.' />
          ),
        },
      ],
    },
    {
      title: 'The opening, without the AI consent',
      heading: 'Changes after design review round 5',
      background: 'custom.gardeniaDark',
      arrows: false,
      steps: [
        {
          id: 'welcome',
          title: 'What we do',
          render: () => chat(INTAKE_CHAT_V2_1),
          isNew: true,
        },
        {
          id: 'welcome-more',
          title: 'Tell me more',
          render: () =>
            chat(INTAKE_CHAT_V2_1_MORE, {
              startAt: INTAKE_CHAT_V2_1_MORE.findIndex(
                (q) => q.id === 'welcome-more',
              ),
            }),
          isNew: true,
        },
      ],
    },
    {
      title: 'Consent to share, after the summary',
      background: 'custom.gardeniaDark',
      beside: true,
      arrows: false,
      steps: [
        {
          id: 'share-consent',
          title: 'Consent, or Power of Attorney',
          render: () =>
            chat(INTAKE_CHAT_V2_1, {
              startAt: INTAKE_CHAT_V2_1.findIndex(
                (q) => q.id === 'share-consent',
              ),
            }),
          // What they said is read by now, so we know who it is for.
          alt: [
            {
              title: "If it's for themselves, not asked",
              render: () =>
                chat(INTAKE_CHAT_V2_1_SELF, {
                  startAt: INTAKE_CHAT_V2_1_SELF.findIndex(
                    (q) => q.id === 'location',
                  ),
                }),
            },
            {
              title: 'If No, reassured and on',
              render: () =>
                chat(INTAKE_CHAT_V2_1_NO_CONSENT, {
                  startAt: INTAKE_CHAT_V2_1_NO_CONSENT.findIndex(
                    (q) => q.id === 'location-no-consent',
                  ),
                }),
            },
          ],
          isNew: true,
        },
        {
          id: 'share-consent-then',
          title: 'Yes, then where they live',
          render: () =>
            chat(INTAKE_CHAT_V2_1, {
              startAt: INTAKE_CHAT_V2_1.findIndex((q) => q.id === 'location'),
            }),
          isNew: true,
        },
      ],
    },
    {
      // Early feedback: they didn't know how long it would take. No stepper:
      // Keni says how long up front, then marks the turning points.
      title: 'How far along they are',
      background: 'custom.gardeniaDark',
      beside: true,
      arrows: false,
      steps: [
        {
          id: 'far-start',
          title: 'How long it takes, up front',
          render: () => chat(INTAKE_CHAT_V2_1),
          isNew: true,
        },
        {
          id: 'far-few-left',
          title: 'After the summary, what is left',
          render: () => chat(INTAKE_CHAT_V2_1, { startAt: at('location') }),
          alt: [
            {
              title: 'What they are',
              render: trial('location', {
                says: [
                  'Thank you. We just need to know where they live, how old they are, and your phone number.',
                ],
              }),
            },
          ],
          isNew: true,
        },
      ],
    },
    {
      title: "If they didn't say what they need help with",
      background: 'custom.gardeniaDark',
      beside: true,
      arrows: false,
      steps: [
        {
          id: 'need-summary',
          title: 'The summary, without it',
          render: () =>
            chat(INTAKE_CHAT_V2_1_NO_NEED, {
              startAt: atIn(INTAKE_CHAT_V2_1_NO_NEED, 'summary'),
              missed: 1,
            }),
        },
        {
          id: 'need-last',
          title: 'Asked as the final question',
          render: () =>
            chat(INTAKE_CHAT_V2_1_NO_NEED, {
              startAt: atIn(INTAKE_CHAT_V2_1_NO_NEED, 'need-help-last'),
              missed: 1,
            }),
        },
        {
          id: 'need-reading',
          title: 'Reading it',
          render: () =>
            chat(INTAKE_CHAT_V2_1_NO_NEED, {
              startAt: atIn(INTAKE_CHAT_V2_1_NO_NEED, 'need-help-last'),
              sent: 1,
              reading: true,
              missed: 1,
            }),
        },
        {
          id: 'need-start',
          title: 'Then where to start',
          render: () =>
            chat(INTAKE_CHAT_V2_1_NO_NEED, {
              startAt: atIn(INTAKE_CHAT_V2_1_NO_NEED, 'start'),
              missed: 1,
            }),
        },
      ],
    },
    {
      // From design review round 5: the keyboard pushed the app bar off the
      // top in prod. The chat area shrinks to fit instead.
      title: 'The keyboard up',
      background: 'custom.gardeniaDark',
      beside: true,
      arrows: false,
      steps: [
        {
          id: 'keyboard-typing',
          title: 'Typing the open question, the app bar stays',
          render: () =>
            chat(INTAKE_CHAT_V2_1, {
              startAt: INTAKE_CHAT_V2_1.findIndex((q) => q.id === 'help'),
              keyboard: true,
              typed: HELP_TYPED,
            }),
          isNew: true,
        },
      ],
    },
    {
      title: 'IDEA: a way out, for A/B testing the form',
      background: 'custom.gardeniaDark',
      beside: true,
      // Hidden for now: shut until somebody opens it.
      collapsed: true,
      arrows: false,
      steps: [
        {
          id: 'exit-offer',
          title: 'Tell me more, with a way out',
          render: () =>
            chat(INTAKE_CHAT_V2_1_EXIT, {
              startAt: INTAKE_CHAT_V2_1_EXIT.findIndex(
                (q) => q.id === 'welcome-more',
              ),
            }),
          isNew: true,
        },
        {
          id: 'exit-form',
          title: 'The form onboarding',
          render: () => (
            <PlaceholderScreen
              title='The form onboarding'
              note="Placeholder. Where I'd rather fill in a form goes: onboarding as a form, the B side of the test."
            />
          ),
          isNew: true,
        },
      ],
    },
  ],
  states: [],
  components: {},
};
